// eslint-disable-next-line no-restricted-imports
import { Disclosure, DisclosureButton, DisclosurePanel } from "@headlessui/react";
import { parseISO } from "date-fns";
import { round } from "lodash-es";
import { type FC, useState } from "react";
import { twMerge } from "tailwind-merge";

import { useFetchPageAssessmentStatuses } from "../course/assessment/assessment-data";
import { getAssessmentStatusLabel } from "../course/assessment/utils";

import type {
  AssessmentDataForPageType,
  InstructorAssessmentDataForCourseType,
  InstructorAssessmentDataForPageType,
  StudentAssessmentDataForCourseType,
  StudentAssessmentDataForPageType,
} from "api/api-data-types";
import { serverGetInstructorAssessments, serverGetStudentAssessments } from "api/api-server-data";
import { LoadingScreen } from "components/LoadingScreen";
import { ASSESSMENT_VIEW_QUERY_PARAM, TERM_ID_QUERY_PARAM } from "components/constants";
import { MarkAssessmentCompleteMenu } from "components/materials/course/assessment/helpers";
import { TermMenu } from "components/menus/TermMenu";
import { ActivityCategory } from "components/server-types";
import { ScrollableTableContainer } from "components/tables/ScrollableTableContainer";
import { TD, TH, Table } from "components/tables/TableElements";
import { t } from "i18n/i18n";
import { Banner } from "mds/components/Banner";
import { Button } from "mds/components/Button";
import { Tooltip } from "mds/components/Tooltip";
import { ChevronDownIcon, ChevronUpIcon, CircleInfoIcon, CommentIcon } from "mds/icons";
import { useAppSelector } from "store/index";
import { selectSortedTermsForCurrentUser } from "store/selectors";
import { selectAssessmentInfoForPage } from "store/selectors/computed";
import { usePromiseEffect } from "store/store-hooks";
import { BUTTON_EVENT_CATEGORY, trackEvent } from "utils/amplitude";
import { Link, useNavigate, useQueryParamsManager } from "utils/routing";
import { HomeRoleType, courseHomeUrl, pageUrl } from "utils/urls";

function pageType(page: AssessmentDataForPageType) {
  if (page.category === "assignment") {
    return t("page_types.assignment");
  }
  if (page.category === "activity_page") {
    if (page.activity_category === ActivityCategory.PREP) {
      return t("page_types.prep_work");
    }
    return t("page_types.activities");
  }

  return t("page_types.other");
}

type InstructorPageRowProps = {
  className?: string;
  courseId: string;
  page: InstructorAssessmentDataForPageType;
  refetch?: () => void;
};

const InstructorPageRow: FC<InstructorPageRowProps> = ({
  className,
  page,
  courseId,
  refetch = () => {},
}) => {
  const isAssessmentComplete =
    page.assessed_submissions_count === page.assessable_submissions_count;
  const isMarkedComplete = Boolean(page.assessing_marked_complete_at);
  const assessmentStatusInfo = useAppSelector((s) => selectAssessmentInfoForPage(s, page?.id));
  const unassessedLateCount = assessmentStatusInfo?.unassessed_late_count ?? 0;

  return (
    <tr className={twMerge("border-b border-black-tint-90", className)}>
      <TD className="max-w-40 truncate px-4 py-2.5" title={page.topic_title || "-"}>
        {page.topic_title || "-"}
      </TD>
      <TD className="flex items-center gap-2 py-2.5" title={page.title}>
        <Link
          className="max-w-40 truncate text-blue-shade-20 underline"
          to={pageUrl(courseId, page.id)}
          onClick={() => {
            trackEvent("Home - Assessments / View Page", {
              ...BUTTON_EVENT_CATEGORY,
              pageTitle: page.title,
              pageUrl: pageUrl(courseId, page.id),
              pageId: page.id,
            });
          }}
        >
          {page.title}
        </Link>
      </TD>

      <TD>
        <Button
          as={Link}
          className="flex items-center gap-1 text-xs"
          eventName="Home - Assessments / Start assessing"
          eventProps={{
            pageTitle: page.title,
            pageUrl: pageUrl(courseId, page.id),
            pageId: page.id,
          }}
          kind={isAssessmentComplete || isMarkedComplete ? "green" : "blue"}
          size="xs"
          to={pageUrl(courseId, page.id, {
            additionalParams: `${ASSESSMENT_VIEW_QUERY_PARAM}=1`,
          })}
        >
          {getAssessmentStatusLabel(
            isMarkedComplete,
            page.assessed_submissions_count,
            isAssessmentComplete,
          )}

          {!!unassessedLateCount && unassessedLateCount > 0 && (
            <Tooltip text={t("assessment.late_assessment_count", { count: unassessedLateCount })}>
              <CircleInfoIcon />
            </Tooltip>
          )}
        </Button>
      </TD>
      <TD className={isAssessmentComplete || isMarkedComplete ? "text-green-shade-40" : undefined}>
        {page.assessed_submissions_count}/{page.assessable_submissions_count}
      </TD>
      <TD
        className={twMerge(
          page.assessments_published_at
            ? isAssessmentComplete
              ? "text-green-shade-40"
              : "text-orange-shade-40"
            : null,
          isMarkedComplete && "text-green-shade-40",
        )}
      >
        {page.assessments_published_at
          ? isAssessmentComplete
            ? t("publishing.published")
            : t("publishing.partial")
          : t("publishing.unpublished")}
      </TD>

      <TD>{pageType(page)}</TD>
      <TD>
        {page.max_points === 0
          ? "–"
          : `${Number.isFinite(page.avg_points) ? round(page.avg_points!, 2) : "–"}/${page.max_points}`}
      </TD>
      <TD>{page.outcomes_count}</TD>
      <TD>
        {page.last_submitted_at
          ? t("common.date", {
              val: parseISO(page.last_submitted_at),
            })
          : null}
      </TD>
      <td>
        <MarkAssessmentCompleteMenu
          courseId={courseId}
          isMarkedComplete={isMarkedComplete}
          pageId={page.id}
          pageTitle={page.title}
          onChange={refetch}
        />
      </td>
    </tr>
  );
};

type StudentPageRowProps = {
  className?: string;
  courseId: string;
  page: StudentAssessmentDataForPageType;
};

const StudentPageRow: FC<StudentPageRowProps> = ({ className, page, courseId }) => (
  <tr className={twMerge("border-b border-black-tint-90", className)}>
    <TD className="max-w-40 truncate px-4 py-2.5" title={page.topic_title || "-"}>
      {page.topic_title || "-"}
    </TD>
    <TD className="flex items-center gap-2 py-2.5" title={page.title}>
      <Link
        className="max-w-40 truncate text-blue-shade-20 underline"
        to={pageUrl(courseId, page.id)}
        onClick={() => {
          trackEvent("Home - View Assessments", {
            ...BUTTON_EVENT_CATEGORY,
            pageTitle: page.title,
            pageUrl: pageUrl(courseId, page.id),
            pageId: page.id,
          });
        }}
      >
        {page.title}
      </Link>
      {page.comment && (
        <Tooltip text={page.comment}>
          <CommentIcon
            aria-label={t("assessment.instructor_comment")}
            className="text-blue-shade-40"
            size="l"
          />
        </Tooltip>
      )}
    </TD>
    <TD>{pageType(page)}</TD>
    <TD>
      {page.points === null || page.max_points === 0 ? "–" : `${page.points}/${page.max_points}`}
    </TD>
    <TD>{page.outcomes_count}</TD>
    <TD>
      {page.submitted_at
        ? t("common.date", {
            val: parseISO(page.submitted_at),
          })
        : null}
    </TD>
  </tr>
);

export const HomeAssessmentList: FC<{ role: HomeRoleType }> = ({ role }) => {
  const navigate = useNavigate();
  const { getQueryParam, setQueryParams } = useQueryParamsManager();
  const sortedTerms = useAppSelector(selectSortedTermsForCurrentUser);
  const selectedTermId = getQueryParam(TERM_ID_QUERY_PARAM) || sortedTerms[0]?.id || "";
  const selectedTermTitle = selectedTermId
    ? sortedTerms.find((term) => term.id === selectedTermId)?.title
    : "";

  const [dummyData, setDummyData] = useState({});
  const refetch = () => setDummyData({}); // Dummy state update to trigger refetch

  const { data, isLoading } = usePromiseEffect<
    InstructorAssessmentDataForCourseType[] | StudentAssessmentDataForCourseType[]
  >(() => {
    if (role === "instructor") {
      return serverGetInstructorAssessments();
    }
    return serverGetStudentAssessments();
  }, [role, dummyData]);

  const pageIds =
    data && role === "instructor"
      ? data.flatMap((course) => course.pages.map((page) => page.id))
      : [];

  useFetchPageAssessmentStatuses(pageIds);

  if (isLoading || data === null) {
    return <LoadingScreen />;
  }

  // Filter courses for the selected term
  const coursesForTerm = selectedTermId
    ? data.filter((course) => course.term_id === selectedTermId)
    : data;

  return (
    <div className="flex h-full flex-col gap-8 overflow-y-auto">
      <TermMenu
        defaultToAll={false}
        menuType="menu"
        selectedTermId={selectedTermId}
        showIcons={false}
        size="s"
        filterToCurrentUser
        onTermChange={(termId) => setQueryParams({ [TERM_ID_QUERY_PARAM]: termId })}
      />
      {coursesForTerm.length === 0 ? (
        <Banner kind="info">
          {t("home.assessments.no_courses_with_assessments_in_term", { term: selectedTermTitle })}
        </Banner>
      ) : (
        <ul className="flex flex-col gap-6">
          {coursesForTerm.map((course) => (
            <li key={course.id}>
              <Disclosure defaultOpen={course.pages.length > 0} key={course.id}>
                {({ open }) => {
                  return (
                    <>
                      <DisclosureButton
                        className="flex w-full items-center justify-between border-b border-black-tint-90 py-2 text-base font-medium text-black-tint-10"
                        title={open ? t("tooltip.collapse") : t("tooltip.expand")}
                      >
                        <Button
                          kind="headerNav"
                          size="s"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            navigate(`${courseHomeUrl(course.id)}?tab=assessments`);
                          }}
                        >
                          {course.code_title}: {course.title}
                        </Button>
                        {open ? <ChevronUpIcon /> : <ChevronDownIcon />}
                      </DisclosureButton>
                      <DisclosurePanel className="overflow-x-hidden">
                        {course.pages.length === 0 ? (
                          <div className="pt-2 text-xs italic text-black-tint-20">
                            {t("home.assessments.no_assessments_for_course")}
                          </div>
                        ) : (
                          <ScrollableTableContainer className="mb-0">
                            <Table>
                              <thead className="border-b border-black-tint-70">
                                <tr>
                                  <TH className="pl-4">{t("glossary.topic")}</TH>
                                  <TH>{t("glossary.page")}</TH>
                                  {role === "instructor" && (
                                    <>
                                      <TH>{t("glossary.status")}</TH>
                                      <TH>{t("glossary.progress")}</TH>
                                      <TH>{t("publishing.title")}</TH>
                                    </>
                                  )}
                                  <TH>{t("page_types.title")}</TH>
                                  <TH>
                                    {role === "instructor"
                                      ? t("glossary.avg_points")
                                      : t("glossary.points")}
                                  </TH>
                                  <TH>{t("glossary.outcomes")}</TH>
                                  <TH>
                                    {role === "instructor"
                                      ? t("glossary.last_submitted")
                                      : t("glossary.submitted")}
                                  </TH>
                                  {role === "instructor" && (
                                    <th className="sr-only">{t("glossary.actions")}</th>
                                  )}
                                </tr>
                              </thead>
                              <tbody>
                                {role === "instructor"
                                  ? (course.pages as InstructorAssessmentDataForPageType[]).map(
                                      (p) => (
                                        <InstructorPageRow
                                          courseId={course.id}
                                          key={p.id}
                                          page={p}
                                          refetch={refetch}
                                        />
                                      ),
                                    )
                                  : (course.pages as StudentAssessmentDataForPageType[]).map(
                                      (p) => (
                                        <StudentPageRow courseId={course.id} key={p.id} page={p} />
                                      ),
                                    )}
                              </tbody>
                            </Table>
                          </ScrollableTableContainer>
                        )}
                      </DisclosurePanel>
                    </>
                  );
                }}
              </Disclosure>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
